import { Fragment, useState, useMemo } from 'react';
export interface SortableColumn<T> {
  key: string;
  label: React.ReactNode;
  width?: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
}
interface SortState {
  key: string | null;
  dir: 'asc' | 'desc';
}
interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  data: T[];
  defaultSort?: {
    key: string;
    dir: 'asc' | 'desc';
  };
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  className?: string;
  renderExpandedRow?: (row: T) => React.ReactNode;
}
function getComparableValue<T>(row: T, col: SortableColumn<T>): string | number {
  if (col.sortValue) return col.sortValue(row);
  const val = (row as Record<string, unknown>)[col.key];
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val;
  return String(val ?? '');
}
function compareValues(a: string | number, b: string | number, dir: 'asc' | 'desc'): number {
  let result: number;
  if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = (a as number) - (b as number);
  }
  return dir === 'asc' ? result : -result;
}
export function SortableTable<T>({
  columns,
  data,
  defaultSort,
  onRowClick,
  rowKey,
  className = 'tbl',
  renderExpandedRow
}: SortableTableProps<T>) {
  const [sort, setSort] = useState<SortState>({
    key: defaultSort?.key ?? null,
    dir: defaultSort?.dir ?? 'desc'
  });
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    return [...data].sort((a, b) => compareValues(getComparableValue(a, col), getComparableValue(b, col), sort.dir));
  }, [data, sort.key, sort.dir, columns]);
  const handleClick = (colKey: string) => {
    setSort(prev => {
      if (prev.key === colKey) {
        if (prev.dir === 'asc') return {
          key: colKey,
          dir: 'desc'
        };
        return {
          key: null,
          dir: 'desc'
        };
      }
      return {
        key: colKey,
        dir: 'asc'
      };
    });
  };
  const sortIndicator = (colKey: string) => {
    const isActive = sort.key === colKey;
    return <span style={{
      marginLeft: 3,
      fontSize: 8,
      color: isActive ? '#fa0f00' : '#c0c5ce'
    }} data-id="element-1190" data-mp="2gaaspw15dwz4">
        {isActive ? sort.dir === 'asc' ? '▲' : '▼' : '▲'}
      </span>;
  };
  return <table className={className} data-id="element-1191" data-mp="38yq9amzxgnck">
      <thead data-id="element-1192" data-mp="38zrjkp94d5t8">
        <tr data-id="element-1193" data-mp="38yyleco87omp">
          {columns.map(col => <th key={col.key} style={{
          width: col.width,
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: col.align
        }} onClick={() => handleClick(col.key)} data-id="element-1194" data-mp="3dx9uumalzx4e">
              {col.label}{sortIndicator(col.key)}
            </th>)}
        </tr>
      </thead>
      <tbody data-id="element-1195" data-mp="id9an0asffd">
        {sortedData.map((row, i) => <Fragment key={rowKey(row) || i} data-id="element-1196" data-mp="k7xca6v8nno" data-mpi={i}>
            <tr onClick={onRowClick ? () => onRowClick(row) : undefined} data-id="element-1197" data-mp="1l8sgszcvhbl9">
              {columns.map(col => <td key={col.key} style={{
            textAlign: col.align
          }} data-id="element-1198" data-mp="1gah2abbmucwp">
                  {col.render(row)}
                </td>)}
            </tr>
            {renderExpandedRow?.(row)}
          </Fragment>)}
      </tbody>
    </table>;
}